import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { BehaviorSubject, catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { CompanyService } from '../company/company.service';
import { User } from '../user/user.types';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _user: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _companyService: CompanyService,
        private _router: Router
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    set isAuthenticated(status: boolean) {
        this._isAuthenticated.next(status);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    get isAuthenticated$(): Observable<boolean> {
        return this._isAuthenticated.asObservable();
    }

    get user$(): Observable<User | null> {
        return this._user.asObservable();
    }

    get user(): User | null {
        return this._user.value;
    }

    set user(user: User | null) {
        this._user.next(user);
        this._userService.authUser = user;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: {
        userName: string;
        password: string;
    }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post('/api/auth/sign-in', credentials).pipe(
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;
                this.isAuthenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;
                this.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    externalSignInPda(token: string): Observable<any> {
        return this._httpClient.post<any>('/api/auth/external-login-pda', { token }).pipe(
            switchMap((response: any) => {
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;
                this.isAuthenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;
                this.user = response.user;

                // Return a new observable with the response
                return of(response);
            }),
        );
    }

    forgotPassword(code: string, channel: 'email' | 'sms'): Observable<any> {
        return this._httpClient.post('/api/auth/forgot-password', { code, channel });
    }

    resetPassword(token: string, password: string): Observable<any> {
        return this._httpClient.post('/api/auth/reset-password', { token, password });
    }

    sendSignInEmail(email: string): Observable<any> {
        // Throw error, if the user is already logged in

        return this._httpClient.post('/api/auth/send-sign-in-email', { email })
    }

    signInWithEmailToken(token: string): Observable<any> {
        // Throw error, if the user is already logged in



        return this._httpClient.post('/api/auth/sign-in-with-email-token', { token })
            .pipe(
                catchError((error) => {
                    // Return false
                    this.isAuthenticated = false;
                    return of(false)
                }
                ),
                switchMap((response: any) => {
                    // Replace the access token with the new one if it's available on
                    // the response object.
                    //
                    // This is an added optional step for better security. Once you sign
                    // in using the token, you should generate a new one on the server
                    // side and attach it to the response object. Then the following
                    // piece of code can replace the token with the refreshed one.
                    if (response.accessToken) {
                        this.accessToken = response.accessToken;
                        this.isAuthenticated = true;
                    }

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;
                    this.user = response.user;

                    // Return true
                    return of(true);
                })
            );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient
            .post('/api/auth/sign-in-with-token', {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError((error) => {
                    // Return false
                    this.isAuthenticated = false;
                    return of(false)
                }
                ),
                switchMap((response: any) => {
                    // Replace the access token with the new one if it's available on
                    // the response object.
                    //
                    // This is an added optional step for better security. Once you sign
                    // in using the token, you should generate a new one on the server
                    // side and attach it to the response object. Then the following
                    // piece of code can replace the token with the refreshed one.
                    if (response.accessToken) {
                        this.accessToken = response.accessToken;
                        this.isAuthenticated = true;
                    }

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;
                    if (response.user && response.user._company) {
                        this._companyService.company = response.user._company;
                    }
                    this.user = response.user;

                    // Return true
                    return of(true);
                })
            );
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    setSession(authResult: { access_token: string }): void {
        localStorage.setItem('accessToken', authResult.access_token);
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;
        this.isAuthenticated = false;
        this._router.navigate(['/sign-in']);
        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('/api/auth/sign-up', user);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    getAuthSystems(userName: string): Observable<any> {
        return this._httpClient.get('/api/auth/check-system', { params: { userName } }).pipe(
            switchMap((response: any) => {
                // // Store the access token in the local storage
                // this.accessToken = response.accessToken;

                // // Set the authenticated flag to true
                // this._authenticated = true;
                // this.isAuthenticated = true;

                // // Store the user on the user service
                // this._userService.user = response.user;

                // // Return a new observable with the response
                return of(response);
            })
        );
    }

    changePassword(currentPassword: string, newPassword: string): Observable<any> {
        return this._httpClient.post('/api/auth/change-password', { currentPassword, newPassword });
    }

    inviteUser(userId: string, channel?: 'email' | 'sms'): Observable<any> {
        return this._httpClient.post(`/api/auth/invite/${userId}`, channel ? { channel } : {});
    }

    acceptInvite(token: string, password: string): Observable<any> {
        return this._httpClient.post('/api/auth/accept-invite', { token, password }).pipe(
            switchMap((response: any) => {
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                    this._authenticated = true;
                    this.isAuthenticated = true;
                    this._userService.user = response.user;
                    this.user = response.user;
                    if (response.user?._company) {
                        this._companyService.company = response.user._company;
                    }
                }
                return of(response);
            })
        );
    }
}
